import { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import API, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { ArrowLeft, Camera, CheckCircle, XCircle, MinusCircle, FilePdf, Warning } from "@phosphor-icons/react";
import { useBrandedPdf } from "../components/hse/useBrandedPdf";
import { OVERALL_CLASS } from "../components/hse/InspectionsPanel";
import { fmtDateTime } from "../lib/dateUtil";

const RESULT_BTN = [["pass", "Pass", CheckCircle, "bg-emerald-600 text-white border-emerald-600", "hover:border-emerald-400"], ["fail", "Fail", XCircle, "bg-red-600 text-white border-red-600", "hover:border-red-400"], ["na", "N/A", MinusCircle, "bg-slate-600 text-white border-slate-600", "hover:border-slate-400"]];

function Item({ item, editable, onResult, onNote, onPhotos, testId }) {
  const fileRef = useRef(null);
  return (
    <div className={`p-3 rounded-md border ${item.result === "fail" ? "border-red-300 bg-red-50/60 dark:bg-red-950/20" : "border-border bg-card"}`} data-testid={testId}>
      <div className="flex items-start gap-2">
        <div className="flex-1">
          <p className="text-sm font-medium leading-snug">{item.text}{item.critical && <Badge className="ml-1.5 text-[9px] bg-red-100 text-red-700 align-middle">critical</Badge>}</p>
          {item.guidance && <p className="text-[11px] text-muted-foreground mt-0.5">{item.guidance}</p>}
        </div>
      </div>
      <div className="flex flex-wrap gap-1.5 mt-2">
        {RESULT_BTN.map(([k, l, Icon, on, hover]) => (
          <button key={k} type="button" disabled={!editable} onClick={() => onResult(k)} aria-pressed={item.result === k} data-testid={`${testId}-${k}`}
            className={`flex items-center gap-1 text-xs px-3 py-2 rounded-md border transition-colors min-w-[72px] justify-center ${item.result === k ? on : `border-border ${editable ? hover : "opacity-70"}`}`}>
            <Icon size={14} weight={item.result === k ? "fill" : "regular"} /> {l}
          </button>
        ))}
        {editable && <>
          <button type="button" onClick={() => fileRef.current?.click()} className="flex items-center gap-1 text-xs px-3 py-2 rounded-md border border-border hover:bg-muted" data-testid={`${testId}-photo`}><Camera size={14} /> Photo{item.photos?.length ? ` (${item.photos.length})` : ""}</button>
          <input ref={fileRef} type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={e => { if (e.target.files?.length) onPhotos(e.target.files); e.target.value = ""; }} />
        </>}
      </div>
      {(item.result === "fail" || item.note) && (
        editable ? <Textarea value={item.note || ""} onChange={e => onNote(e.target.value)} rows={2} placeholder="What's wrong? What did you do about it?" className="mt-2 text-sm" data-testid={`${testId}-note`} />
          : <p className="mt-2 text-sm text-muted-foreground">{item.note}</p>
      )}
      {!!item.photos?.length && (
        <div className="flex flex-wrap gap-1.5 mt-2">
          {item.photos.map(p => <a key={p.id} href={`${process.env.REACT_APP_BACKEND_URL}${p.file_url}`} target="_blank" rel="noreferrer"><img src={`${process.env.REACT_APP_BACKEND_URL}${p.file_url}`} alt="" className="h-16 w-16 object-cover rounded border border-border" /></a>)}
        </div>
      )}
    </div>
  );
}

/** Run an inspection on a phone (pass / fail / n/a, notes, photos, sign-off) — or review a completed one. */
export default function InspectionPage() {
  const { id } = useParams();
  const { pathname } = useLocation();
  const portal = pathname.startsWith("/my-work");
  const backTo = portal ? "/my-work" : "/health-safety?tab=inspections";
  const [insp, setInsp] = useState(null);
  const [sig, setSig] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const pending = useRef({});
  const timer = useRef(null);

  const load = useCallback(() => API.get(`/hse/inspections/${id}`).then(({ data }) => {
    const local = pending.current;
    setInsp(Object.keys(local).length ? { ...data, sections: data.sections.map(s => ({ ...s, items: s.items.map(i => (local[i.id] ? { ...i, ...local[i.id] } : i)) })) } : data);
    setNotes(n => n || data.notes || "");
  }).catch(e => toast.error(formatApiError(e))), [id]);
  useEffect(() => { load(); }, [load]);

  const flush = useCallback(async () => {
    const results = Object.values(pending.current);
    if (!results.length) return;
    pending.current = {};
    try { await API.put(`/hse/inspections/${id}`, { results }); } catch (e) { toast.error(formatApiError(e)); }
  }, [id]);
  const queue = (itemId, patch) => {
    setInsp(d => ({ ...d, sections: d.sections.map(s => ({ ...s, items: s.items.map(i => (i.id === itemId ? { ...i, ...patch } : i)) })) }));
    pending.current[itemId] = { ...(pending.current[itemId] || { item_id: itemId }), ...patch };
    clearTimeout(timer.current); timer.current = setTimeout(flush, 600);
  };
  const photos = async (itemId, files) => {
    const fd = new FormData(); fd.append("item_id", itemId); [...files].forEach(f => fd.append("files", f));
    try { const { data } = await API.post(`/hse/inspections/${id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } }); setInsp(d => ({ ...d, sections: d.sections.map(s => ({ ...s, items: s.items.map(i => (i.id === itemId ? { ...i, photos: data.photos } : i)) })) })); toast.success("Photo added"); } catch (e) { toast.error(formatApiError(e)); }
  };
  const complete = async () => {
    setBusy(true);
    try { await flush(); const { data } = await API.post(`/hse/inspections/${id}/complete`, { signature_name: sig, notes }); setInsp(data); toast.success(`Inspection ${data.overall === "pass" ? "passed" : "completed — " + data.actions.length + " action(s) raised"}`); window.scrollTo(0, 0); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  const { openBranded, brandingDialog } = useBrandedPdf();
  const pdf = () => openBranded({ url: `/hse/inspections/${id}/pdf`, filename: `${insp.reference}.pdf`, title: `${insp.reference} report branding` });
  const closeAction = async (a) => { try { const { data } = await API.put(`/hse/inspections/${id}/actions/${a.id}`, { status: a.status === "done" ? "open" : "done" }); setInsp(data); } catch (e) { toast.error(formatApiError(e)); } };

  if (!insp) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;
  const editable = insp.status === "in_progress";
  const items = insp.sections.flatMap(s => s.items);
  const unanswered = items.filter(i => !i.result).length;
  const sc = { total: items.length, answered: items.length - unanswered, fail: items.filter(i => i.result === "fail").length };

  return (
    <div className="p-4 sm:p-6 max-w-3xl mx-auto space-y-4 pb-36 md:pb-6" data-testid="inspection-page">
      <div className="flex items-start gap-3">
        <Link to={backTo} className="mt-1 text-muted-foreground hover:text-foreground" data-testid="insp-back"><ArrowLeft size={18} /></Link>
        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground"><span className="font-semibold">{insp.reference}</span><Badge className={`text-[10px] ${OVERALL_CLASS[insp.overall || ""]}`} data-testid="insp-status">{editable ? "in progress" : insp.overall}</Badge>{insp.location && <span>· {insp.location}</span>}</div>
          <h1 className="text-xl sm:text-2xl font-[Chivo] font-bold tracking-tight">{insp.title}</h1>
          <p className="text-xs text-muted-foreground">{insp.inspector?.name} · {editable ? `started ${fmtDateTime(insp.started_at)}` : `completed ${fmtDateTime(insp.completed_at)} · signed ${insp.signature_name}`}</p>
        </div>
        {!editable && <Button size="sm" variant="outline" onClick={pdf} data-testid="insp-pdf-btn"><FilePdf size={14} className="mr-1" /> Report</Button>}
      </div>

      {!editable && insp.actions?.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 dark:bg-amber-950/20 rounded-lg p-3" data-testid="insp-actions">
          <p className="text-xs font-semibold flex items-center gap-1 mb-2"><Warning size={14} weight="fill" className="text-amber-600" /> Actions raised</p>
          {insp.actions.map(a => (
            <label key={a.id} className="flex items-start gap-2 text-sm py-1" data-testid={`insp-action-${a.id}`}>
              {!portal ? <input type="checkbox" checked={a.status === "done"} onChange={() => closeAction(a)} className="mt-1" data-testid={`insp-action-toggle-${a.id}`} /> : <span className="mt-1 text-[10px] uppercase font-semibold">{a.status}</span>}
              <span className={a.status === "done" ? "line-through text-muted-foreground" : ""}>{a.critical && <span className="text-red-600 font-semibold">CRITICAL · </span>}{a.text}</span>
            </label>
          ))}
        </div>
      )}

      {insp.sections.map((s, si) => (
        <section key={s.id} className="space-y-2" data-testid={`insp-section-${si}`}>
          <h2 className="font-[Chivo] font-semibold text-sm text-muted-foreground uppercase tracking-wider">{si + 1}. {s.heading}</h2>
          {s.items.map((it, ii) => <Item key={it.id} item={it} editable={editable} onResult={r => queue(it.id, { result: r })} onNote={n => queue(it.id, { note: n })} onPhotos={f => photos(it.id, f)} testId={`insp-item-${si}-${ii}`} />)}
        </section>
      ))}

      {editable ? (
        <div className="bg-card border border-border rounded-lg p-3 mb-2" data-testid="insp-complete-bar">
          <div className="space-y-2">
            <p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Sign off</p>
            <div className="flex items-center gap-2 text-xs"><div className="flex-1 h-2 rounded-full bg-muted overflow-hidden"><div className="h-full bg-primary transition-all" style={{ width: `${(sc.answered / Math.max(1, sc.total)) * 100}%` }} /></div><span data-testid="insp-progress">{sc.answered}/{sc.total}{sc.fail ? ` · ${sc.fail} fail` : ""}</span></div>
            <div className="flex flex-wrap gap-2">
              <Input value={notes} onChange={e => setNotes(e.target.value)} placeholder="Overall notes (optional)" className="h-9 flex-1 min-w-[160px]" data-testid="insp-notes" />
              <Input value={sig} onChange={e => setSig(e.target.value)} placeholder="Type your name to sign" className="h-9 w-44" data-testid="insp-signature" />
              <Button onClick={complete} disabled={busy || unanswered > 0 || !sig.trim()} data-testid="insp-complete-btn">{unanswered ? `${unanswered} left` : busy ? "Saving…" : "Complete"}</Button>
            </div>
          </div>
        </div>
      ) : insp.notes && <div className="bg-card border border-border rounded-md p-3 text-sm" data-testid="insp-notes-view"><p className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground mb-1">Inspector's notes</p>{insp.notes}</div>}
      {brandingDialog}
    </div>
  );
}
